import {
  categoryForMetric,
  EventLogMetricCategory,
  labelForMetric,
} from '@chiroup/core/constants/metricCategories';
import {
  CalendarDays,
  ChevronDown,
  Clipboard,
  Database,
  DollarSign,
  MapPin,
  Settings,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useEventLogEvents from '../../hooks/useEventLogEvents';
import { metricKeyToEventType } from './metricToEventType';

type Props = {
  open: boolean;
  onClose: () => void;
  clinicId: number;
  metricKey: string | null;
  startDate: string;
  endDate: string;
  userId?: string;
};

const CATEGORY_ICONS: Record<
  EventLogMetricCategory,
  React.ComponentType<{ className?: string }>
> = {
  schedule: CalendarDays,
  clinical: Clipboard,
  billing: DollarSign,
  patients: Users,
  databases: Database,
  clinic_settings: Settings,
  user_settings: Users,
  resources: Users,
};

const CATEGORY_HEADER_COLORS: Record<
  EventLogMetricCategory,
  { bg: string; icon: string; badge: string }
> = {
  schedule: {
    bg: 'bg-gradient-to-r from-green-50 to-white',
    icon: 'text-green-600 bg-green-100',
    badge: 'bg-green-100 text-green-700',
  },
  clinical: {
    bg: 'bg-gradient-to-r from-blue-50 to-white',
    icon: 'text-blue-600 bg-blue-100',
    badge: 'bg-blue-100 text-blue-700',
  },
  billing: {
    bg: 'bg-gradient-to-r from-amber-50 to-white',
    icon: 'text-amber-600 bg-amber-100',
    badge: 'bg-amber-100 text-amber-700',
  },
  patients: {
    bg: 'bg-gradient-to-r from-rose-50 to-white',
    icon: 'text-rose-600 bg-rose-100',
    badge: 'bg-rose-100 text-rose-700',
  },
  databases: {
    bg: 'bg-gradient-to-r from-indigo-50 to-white',
    icon: 'text-indigo-600 bg-indigo-100',
    badge: 'bg-indigo-100 text-indigo-700',
  },
  clinic_settings: {
    bg: 'bg-gradient-to-r from-cyan-50 to-white',
    icon: 'text-cyan-600 bg-cyan-100',
    badge: 'bg-cyan-100 text-cyan-700',
  },
  user_settings: {
    bg: 'bg-gradient-to-r from-purple-50 to-white',
    icon: 'text-purple-600 bg-purple-100',
    badge: 'bg-purple-100 text-purple-700',
  },
  resources: {
    bg: 'bg-gradient-to-r from-orange-50 to-white',
    icon: 'text-orange-600 bg-orange-100',
    badge: 'bg-orange-100 text-orange-700',
  },
};

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-rose-500',
  'bg-amber-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-pink-500',
];

const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();

  return name.slice(0, 2).toUpperCase();
};

const formatTimestamp = (ms: number): { date: string; time: string } => {
  const d = new Date(ms);

  return {
    date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  };
};

const METADATA_LABEL_MAP: Record<string, string> = {
  appointmentId: 'Appt',
  patientId: 'Patient',
  encounterId: 'Encounter',
  invoiceId: 'Invoice',
  transactionId: 'Txn',
  providerId: 'Provider',
  payorId: 'Payor',
  claimId: 'Claim',
};

const isLongValue = (val: unknown): boolean => typeof val === 'string' && val.length > 20;

const truncateValue = (val: unknown): string => {
  if (typeof val === 'string') {
    if (val.length > 20) return val.slice(0, 10) + '..';

    return val;
  }

  return String(val);
};

const MetaPill: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  const long = isLongValue(value);
  const [expanded, setExpanded] = useState(false);
  const full = String(value);

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[10px] text-gray-500 ${long ? 'cursor-pointer hover:bg-gray-200' : ''} ${expanded ? 'break-all' : ''}`}
      title={full}
      onClick={
        long
          ? e => {
              e.stopPropagation();
              setExpanded(v => !v);
            }
          : undefined
      }
    >
      <span className="text-gray-400">{label}:</span>
      {expanded ? full : truncateValue(value)}
    </span>
  );
};

const MetadataPillsInline: React.FC<{ metadata: Record<string, unknown> }> = ({ metadata }) => {
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return null;

  return (
    <>
      {entries.map(([key, val]) => (
        <MetaPill key={key} label={METADATA_LABEL_MAP[key] || key} value={val} />
      ))}
    </>
  );
};

const SkeletonRow: React.FC = () => (
  <div className="px-4 py-2 flex items-center gap-2.5">
    <div className="h-6 w-6 rounded-full skeleton-shimmer flex-shrink-0" />
    <div className="flex-1 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-24 rounded skeleton-shimmer" />
        <div className="h-3.5 w-14 rounded skeleton-shimmer" />
      </div>
      <div className="h-3 w-20 rounded skeleton-shimmer" />
    </div>
  </div>
);

const MetricDrawer: React.FC<Props> = ({
  open,
  onClose,
  clinicId,
  metricKey,
  startDate,
  endDate,
  userId,
}) => {
  const [visible, setVisible] = useState(false);

  // Animate in
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const eventType = metricKey ? metricKeyToEventType(metricKey) : undefined;

  const { data, isFetching, fetchNextPage, isFetchingNextPage, hasNextPage } = useEventLogEvents(
    clinicId,
    { eventType, startDate, endDate, userId },
    open && !!eventType
  );

  if (!open || !metricKey) return null;

  const events = data?.pages?.flatMap(p => p.data || []) || [];
  const total = data?.pages?.[0]?.count ?? 0;
  const category = categoryForMetric(metricKey);
  const headerColors = CATEGORY_HEADER_COLORS[category];
  const Icon = CATEGORY_ICONS[category];

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/30 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`ml-auto relative h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className={`border-b border-gray-200 px-4 py-3 ${headerColors.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`p-1.5 rounded-lg ${headerColors.icon} flex-shrink-0`}>
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {labelForMetric(metricKey)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${headerColors.badge}`}
                  >
                    {total.toLocaleString()} {total === 1 ? 'event' : 'events'}
                  </span>
                  {userId && <span className="text-[10px] text-gray-400">filtered to user</span>}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 -m-1 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Event list */}
        <div className="flex-1 overflow-y-auto">
          {isFetching && events.length === 0 ? (
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 20 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="px-5 py-16 text-center">
              <div className="text-gray-300 mb-2">
                <Clipboard className="h-10 w-10 mx-auto" />
              </div>
              <p className="text-sm text-gray-500">No events found</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting the date range or filters</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {events.map(ev => {
                const ts = formatTimestamp(ev.occurredAt);
                const displayName = ev.userName || ev.userId || 'System';

                return (
                  <li
                    key={ev.id}
                    className="px-4 py-2 hover:bg-gray-50/80 transition-colors duration-150"
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0 ${getAvatarColor(displayName)}`}
                      >
                        {getInitials(displayName)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-900 truncate">
                            {displayName}
                          </span>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                            {ts.date} · {ts.time}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {ev.locationId != null && (
                            <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[10px] text-gray-500">
                              <MapPin className="h-2.5 w-2.5" />
                              {ev.locationId}
                            </span>
                          )}
                          {ev.metadata && <MetadataPillsInline metadata={ev.metadata} />}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Load more */}
          {hasNextPage && (
            <div className="px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50"
              >
                {isFetchingNextPage ? (
                  <span className="text-gray-500">Loading...</span>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Load more events
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MetricDrawer;
